/*#
# MCP: the verbs as an agent's tools

For many agents MCP *is* the API, so the [verb table](/verbs/) is served as
an MCP server: every verb is a tool, its tosijs-schema input is the tool's
JSON Schema, and a call is `callVerb` — validated at the boundary, never
throwing, an `{ ok: false }` becoming a tool error rather than a crash. The
protocol layer here is transport-free (a JSON-RPC message in, a response
out) so it can sit on stdio, a WebSocket, or a test. `virta-mcp` (from this
repo: `bun run mcp`) is the stdio server:

```
virta-mcp --host https://us-central1-<project>.cloudfunctions.net
virta-mcp --memory --seed 40             # a throwaway board, for trying it
virta-mcp --read-only                    # query, get, transcript, projects only
virta-mcp --allow query,get,comment      # exactly these verbs
```

On a host, a token belongs to a **machine** (its label, `Tosi`), shared by
every repo there. So every write tool takes a required `as`:
`"Tosi × tosijs-3d"`, meaning who the agent is acting as. The server refuses an `as`
for another machine, the write carries it as `by`, and the host stamps its
own `_by` (the machine's token) beside it (board #171). The read tools need
no `as`. Every call syncs the replica first, so
reads see other writers' events and a write never decides against a stale
view; when the host cannot be reached a read answers from the replica,
prefixed `(replica may be stale …)`, and a write is refused until a call
finds the host back.
Registering it with an MCP client is the usual shape (this repo's
`.mcp.json` runs `bun bin/mcp.ts` instead, and finds the host from the
stored token):

```
{ "mcpServers": { "virta": { "command": "virta-mcp",
    "args": ["--host", "https://us-central1-<project>.cloudfunctions.net", "--read-only"] } } }
```

Tools only, for now: the transcript verb already hands an agent the
unfolded event stream, which is what the design says agents get. Resources
(a task as `virta://task/38`) would be a reader's convenience on top;
parked until an agent asks for it.
*/

/*{ "parent": "Reference", "order": 99 }*/

import { isAuthRefusal } from './cached-log'
import {
  bytesFromBase64,
  downloadFile,
  uploadFile,
  type FileReader,
  type FileStore,
} from './files'
import { READ_VERBS, callVerb, VERBS, verbCatalog, verbForTool } from './verbs'
import { guide } from './guidance'
import { duplicateHint } from './similar'
import type { Attachment, Task } from './model'
import type { VirtaStore } from './store'

export const MCP_PROTOCOL_VERSION = '2025-06-18'
/** The Library's root as an MCP resource (board #2497); topics are `/<topic>` under it. */
export const GUIDANCE_URI = 'virta://guidance'

export interface JsonRpcRequest {
  jsonrpc: '2.0'
  id?: number | string | null
  method: string
  params?: unknown
}

export interface JsonRpcResponse {
  jsonrpc: '2.0'
  id: number | string | null
  result?: unknown
  error?: { code: number; message: string; data?: unknown }
}

/** JSON-RPC error codes the server uses. */
export const RPC = {
  parse: -32700,
  invalidRequest: -32600,
  methodNotFound: -32601,
  invalidParams: -32602,
  internal: -32603,
} as const

export interface McpServerOptions {
  store: VirtaStore
  /**
   * This server's token id: \`reattribute\` with no token or events
   * corrects this one (board #2752), so an agent needn't know its id.
   */
  tokenId?: string
  name?: string
  version?: string
  /**
   * Called before a read verb runs, so a replica catches up with other
   * writers first. Optional: a memory board has nothing to catch up on.
   */
  /**
   * Run before every tool call (a sync of the replica): reads see other
   * writers, and a write never decides against a view the host has moved
   * past — the log refuses a push while stale, and this is what clears it.
   * A refused credential from here is a tool error, an outage a caveat.
   */
  beforeCall?: () => Promise<void>
  /**
   * The machine this server's token belongs to. When given, every write
   * tool requires `as: "<machine> × <repo>"` — who the agent is acting as
   * (owner, 2026-09-26; board #171). The host verifies the machine (the
   * token); the repo is the agent's word, stated on every write rather
   * than guessed from a working directory. An `as` naming another machine
   * is refused.
   */
  actingMachine?: string
  /**
   * Where files go (board #2436): the host's `/blob`. With it, `attach`
   * and `showcase` take a file's contents (base64) and the server stores
   * it; without it (a memory board), they take links only. It also reads
   * them back: `attachments` with `content: true` (board #3560).
   */
  files?: FileStore & FileReader
  /**
   * `readOnly` serves the read verbs only; `allow` names exactly the verbs
   * served — the two are alternatives, and both together is refused at
   * construction. A client on `.mcp.json` otherwise holds the whole table —
   * including `import` and `notes`, which write thousands of events in one
   * call.
   */
  readOnly?: boolean
  allow?: string[]
}

/** Verbs that only read; the server syncs before these. */

/**
 * What an options pair serves, for a startup line. One resolution, shared
 * by the constructor below — the announcement and the gate can never
 * disagree (0.5.0 re-review 7: they did, with opposite precedence).
 */
export const mcpMode = (readOnly?: boolean, allow?: string[]): string =>
  readOnly
    ? 'read-only'
    : allow && allow.length
    ? `allow: ${allow.join(', ')}`
    : 'every verb'

/**
 * The MCP protocol over a store. `handle` takes one decoded JSON-RPC message
 * and returns the response, or `null` for a notification (no id) — the
 * caller owns the transport.
 */
export class McpServer {
  private readonly store: VirtaStore
  private readonly name: string
  private readonly version: string
  private readonly beforeCall: (() => Promise<void>) | undefined
  private readonly allowed: ReadonlySet<string> | null
  private readonly actingMachine: string | undefined
  private readonly tokenId: string | undefined
  /** Tool calls run one at a time: a call's `as` is the store's identity while it runs. */
  private calls: Promise<unknown> = Promise.resolve()
  /** What this server serves, from the same resolution the constructor used. */
  readonly mode: string

  constructor(options: McpServerOptions) {
    // enforce at construction, never merely document: with both given the
    // served set was resolved twice with opposite precedence (re-review 7);
    // an empty allow list and an unknown verb would announce what the gate
    // cannot serve (re-review 8)
    if (options.readOnly && options.allow && options.allow.length)
      throw new Error('readOnly and allow are alternatives; give one')
    if (options.allow && options.allow.length === 0)
      throw new Error('allow must name at least one verb')
    for (const v of options.allow ?? [])
      if (!Object.hasOwn(VERBS, v))
        throw new Error(`allow names an unknown verb "${v}"`)
    this.store = options.store
    this.name = options.name ?? 'virta'
    this.version = options.version ?? '0.1.0'
    this.beforeCall = options.beforeCall
    this.actingMachine = options.actingMachine
    this.tokenId = options.tokenId
    this.files = options.files
    this.allowed = options.allow
      ? new Set(options.allow)
      : options.readOnly
      ? READ_VERBS
      : null
    this.mode = mcpMode(options.readOnly, options.allow)
  }

  private serves(name: string): boolean {
    return this.allowed === null || this.allowed.has(name)
  }

  /** Decode a line of the stdio transport; a bad line is a parse error with a null id. */
  async handleText(text: string): Promise<JsonRpcResponse | null> {
    let message: unknown
    try {
      message = JSON.parse(text)
    } catch {
      return {
        jsonrpc: '2.0',
        id: null,
        error: { code: RPC.parse, message: 'parse error' },
      }
    }
    return this.handle(message)
  }

  async handle(message: unknown): Promise<JsonRpcResponse | null> {
    if (!isRequest(message)) {
      return {
        jsonrpc: '2.0',
        id: null,
        error: { code: RPC.invalidRequest, message: 'invalid request' },
      }
    }
    const id = message.id ?? null
    const notification = message.id === undefined
    const reply = (result: unknown): JsonRpcResponse | null =>
      notification ? null : { jsonrpc: '2.0', id, result }
    const fail = (
      code: number,
      text: string,
      data?: unknown
    ): JsonRpcResponse | null =>
      notification
        ? null
        : { jsonrpc: '2.0', id, error: { code, message: text, data } }
    try {
      switch (message.method) {
        case 'initialize':
          return reply({
            protocolVersion: MCP_PROTOCOL_VERSION,
            capabilities: {
              tools: { listChanged: false },
              resources: { listChanged: false },
            },
            serverInfo: { name: this.name, version: this.version },
            instructions:
              'virta is a task board: tags are the model (`key:value` or bare), `project:<name>` says whose task it is, `status:` is the lane (inbox is implicit — no status tag). Use `query` to find tasks, `get` for one, `transcript` for its full history, and the write verbs to change it. Every write is attributed to you: on a host, the write tools take `as` — "<machine> × <repo>", the repo you are working for.',
          })
        case 'notifications/initialized':
        case 'notifications/cancelled':
          return null
        case 'ping':
          return reply({})
        case 'tools/list':
          return reply({
            tools: verbCatalog()
              .filter((v) => this.serves(v.name))
              .map((v) => ({
                name: v.tool,
                description: v.description,
                inputSchema: withDownloads(
                  v.name,
                  withUploads(
                    v.name,
                    this.needsAs(v.name)
                      ? withAs(v.inputSchema, this.actingMachine!)
                      : v.inputSchema
                  )
                ),
              })),
          })
        // the Library's way in as resources (board #2497): the root, and
        // a page per topic (?project=<name> for a project's precedence)
        case 'resources/list': {
          await this.syncQuietly()
          const g = guide(this.store)
          return reply({
            resources: [
              {
                uri: GUIDANCE_URI,
                name: 'guidance',
                description:
                  'Start here: the root guidance entry and every topic with its index (the Library’s llms.txt).',
                mimeType: 'text/markdown',
              },
              ...g.topics.map((t) => ({
                uri: `${GUIDANCE_URI}/${encodeURIComponent(t.topic)}`,
                name: `guidance: ${t.topic}`,
                description: `${t.topic}: ${
                  t.index ? t.index.title : 'no index yet'
                } (${
                  t.count
                } entries; add ?project=<name> for that project's precedence)`,
                mimeType: 'text/markdown',
              })),
            ],
          })
        }
        case 'resources/read': {
          const uri = String(
            (message.params as { uri?: unknown } | undefined)?.uri ?? ''
          )
          const m =
            /^virta:\/\/guidance(?:\/([^?]*))?(?:\?project=(.*))?$/.exec(uri)
          if (!m) return fail(RPC.invalidParams, `no resource ${uri}`)
          await this.syncQuietly()
          const g = guide(this.store, {
            topic: decodeURIComponent(m[1] ?? ''),
            project: decodeURIComponent(m[2] ?? ''),
          })
          return reply({
            contents: [{ uri, mimeType: 'text/markdown', text: g.text }],
          })
        }
        case 'tools/call': {
          const run = this.calls.then(() => this.call(message, reply, fail))
          this.calls = run.catch(() => undefined)
          return await run
        }
        default:
          return fail(RPC.methodNotFound, `unknown method ${message.method}`)
      }
    } catch (e) {
      return fail(RPC.internal, e instanceof Error ? e.message : String(e))
    }
  }

  private readonly files: (FileStore & FileReader) | undefined

  /** Sync before a read when this server can; a failure leaves the replica to answer. */
  private async syncQuietly(): Promise<void> {
    try {
      await this.beforeCall?.()
    } catch {
      // a resource is a read: the replica answers
    }
  }

  /**
   * A file's contents in a call (board #2436): checked, stored through the
   * host's /blob with this server's credential, and replaced by the stored
   * reference the verb takes — the same uploadFile the board and the CLI use.
   */
  private async storeUploads(
    verb: string,
    args: Record<string, unknown>
  ): Promise<Record<string, unknown>> {
    if (!Object.hasOwn(UPLOADS, verb)) return args
    const u = UPLOADS[verb]!
    const content = args[u.content]
    if (typeof content !== 'string') return args
    if (!this.files)
      throw new Error(
        'this server has no host to store files in (a memory board): attach a link instead'
      )
    const name = String(args[u.name] ?? '')
    if (!name)
      throw new Error(
        `${verb} with ${u.content} needs ${u.name}: the file's name`
      )
    const stored = await uploadFile(
      this.files,
      name,
      bytesFromBase64(content),
      typeof args[u.type] === 'string' ? (args[u.type] as string) : ''
    )
    const rest = { ...args }
    delete rest[u.content]
    delete rest[u.type]
    if (u.name !== 'name') delete rest[u.name]
    return { ...rest, [u.into]: stored }
  }

  /**
   * Each stored file's contents beside its reference (board #3560): text as
   * text, anything else as base64, checked against its sha256. A file over
   * INLINE_MAX_BYTES, or one the host won't give, says why instead.
   */
  private async inlineFiles(
    list: Attachment[]
  ): Promise<Array<Attachment & Record<string, unknown>>> {
    const out: Array<Attachment & Record<string, unknown>> = []
    for (const a of list) {
      if (!a.file) {
        out.push({ ...a })
        continue
      }
      if (!this.files) {
        out.push({
          ...a,
          error: 'this server has no host to read files from (a memory board)',
        })
        continue
      }
      if (a.file.bytes > INLINE_MAX_BYTES) {
        out.push({
          ...a,
          error: `${a.file.bytes} bytes is more than ${INLINE_MAX_BYTES} to inline: from a shell, virta files <id> --out <dir>`,
        })
        continue
      }
      try {
        const bytes = await downloadFile(this.files, a.file)
        out.push(
          isTextType(a.file.type)
            ? { ...a, text: new TextDecoder().decode(bytes) }
            : { ...a, base64: Buffer.from(bytes).toString('base64') }
        )
      } catch (e) {
        out.push({ ...a, error: e instanceof Error ? e.message : String(e) })
      }
    }
    return out
  }

  private needsAs(verb: string): boolean {
    return this.actingMachine !== undefined && !READ_VERBS.has(verb)
  }

  /** One tool call: the gate, the sync, who it acts as, the verb. */
  private async call(
    message: JsonRpcRequest,
    reply: (result: unknown) => JsonRpcResponse | null,
    fail: (code: number, text: string, data?: unknown) => JsonRpcResponse | null
  ): Promise<JsonRpcResponse | null> {
    const params = (message.params ?? {}) as {
      name?: unknown
      arguments?: unknown
    }
    if (typeof params.name !== 'string')
      return fail(RPC.invalidParams, 'tools/call needs a tool name')
    // a tool's name, or its verb's own (the name it had before board #2440)
    const verb: string = verbForTool(params.name) ?? params.name
    if (!this.serves(verb))
      return reply(
        toolError(
          `"${verb}" is not served by this server (read-only or allow-listed)`
        )
      )
    let stale = ''
    if (this.beforeCall) {
      try {
        await this.beforeCall()
      } catch (e) {
        // a refused credential is not an outage: no data, say what to do
        if (isAuthRefusal(e))
          return reply(
            toolError(
              `the host refused this token (${
                e instanceof Error ? e.message : String(e)
              }); run \`virta self login\` for a new one`
            )
          )
        // a stale read is better than none: answer from the replica, and say so
        stale = `(replica may be stale: could not sync — ${
          e instanceof Error ? e.message : String(e)
        })\n`
      }
    }
    // a CachedLog absorbs a host failure (the replica answers) instead
    // of throwing: the caveat must come from the log, not the sync
    const log = this.store.log as { stale?: boolean; lastError?: unknown }
    if (READ_VERBS.has(verb) && !stale && log.stale) {
      const e = log.lastError
      stale = `(replica may be stale: the host is unreachable — ${
        e instanceof Error ? e.message : String(e)
      })\n`
    }
    // a write says who it acts as; the machine must be this token's
    let args = params.arguments ?? {}
    let as: string | null = null
    if (this.needsAs(verb)) {
      const given =
        args && typeof args === 'object'
          ? (args as { as?: unknown }).as
          : undefined
      const machine = this.actingMachine!
      if (
        typeof given !== 'string' ||
        !given.startsWith(`${machine} × `) ||
        !given.slice(machine.length + 3).trim()
      )
        return reply(
          toolError(
            `"${verb}" writes, so it needs as: "${machine} × <repo>" — who you are acting as: this machine (${machine}, the token's) and the repo you are working for${
              typeof given === 'string' ? ` (got "${given}")` : ''
            }`
          )
        )
      as = given.trim()
      const { as: _as, ...rest } = args as Record<string, unknown>
      void _as
      args = rest
    }
    // attachments' content: true is this server's, not the verb's (#3560)
    let inline = false
    if (verb === 'attachments') {
      const { content, ...rest } = args as Record<string, unknown>
      inline = content === true
      args = rest
    }
    // a task filed for an enrolled repo is that repo's unless it names a
    // project (boards #1082, #2431); otherwise it is on no board — say so
    let note = ''
    // a file's contents: stored first, then named (board #2436)
    try {
      args = await this.storeUploads(verb, args as Record<string, unknown>)
    } catch (e) {
      return reply(toolError(e instanceof Error ? e.message : String(e)))
    }
    if (verb === 'create' && as) {
      const given = (args as { tags?: unknown }).tags
      const repo = as.slice(this.actingMachine!.length + 3).trim()
      const home = this.store.homeFor(
        Array.isArray(given) ? given.map(String) : [],
        repo
      )
      args = { ...args, tags: home.tags }
      if (home.orphan)
        note = `(filed with no project, so it is on no board: ${repo} is not enrolled — add one with the move tool)\n`
    }
    // a brief doesn't report this server's own writes back (#2733)
    if (
      verb === 'brief' &&
      (args as { token?: unknown }).token === undefined &&
      this.tokenId
    )
      args = { ...args, token: this.tokenId }
    // publishing a skill is checked against this server's own token (#2477):
    // always its own, never one the caller names
    if (verb === 'publishSkill' && this.tokenId)
      args = { ...args, token: this.tokenId }
    // reattribute with nothing named corrects this server's own token
    if (
      verb === 'reattribute' &&
      (args as { token?: unknown }).token === undefined &&
      (args as { events?: unknown }).events === undefined &&
      this.tokenId
    )
      args = { ...args, token: this.tokenId }
    const outcome = as
      ? await this.store.runAs(as, () =>
          callVerb(this.store, verb as string, args)
        )
      : await callVerb(this.store, verb, args)
    if (!outcome.ok) {
      return reply(
        toolError(
          (outcome.issues?.length
            ? `${outcome.error}: ${outcome.issues.join('; ')}`
            : outcome.error) +
            (outcome.error.startsWith('StaleReplicaError')
              ? ' (this server syncs before every call: retry once the host is back)'
              : '')
        )
      )
    }
    let result = outcome.result
    if (inline) result = await this.inlineFiles(result as Attachment[])
    // filing says what looks like it (board #2732)
    if (verb === 'create') {
      const alike = duplicateHint(this.store.tasks, result as Task)
      if (alike.length) note += `(${alike.join('\n')})\n`
    }
    return reply({
      content: [
        {
          type: 'text',
          text: stale + note + JSON.stringify(result, null, 2),
        },
      ],
      structuredContent: Array.isArray(result)
        ? { items: result }
        : (result as unknown as Record<string, unknown>),
    })
  }
}

/**
 * The verbs that take a file's contents over MCP (board #2436), and the
 * fields that carry them: JSON has no bytes, so a file travels as base64,
 * with its name (its type follows from the name unless given).
 */
const UPLOADS: Record<
  string,
  { content: string; name: string; type: string; into: string; what: string }
> = {
  attach: {
    content: 'content',
    name: 'name',
    type: 'contentType',
    into: 'file',
    what: 'the file to attach (base64): a screenshot, a log, a skill or checklist (.md), a PDF; up to 25 MB. With it, name is the file name; leave url out',
  },
  showcase: {
    content: 'mediaContent',
    name: 'mediaName',
    type: 'mediaType',
    into: 'file',
    what: "the showcase's picture (base64), e.g. a screen capture; instead of media (a URL)",
  },
}

/** A tool's schema with its upload fields, where it takes a file. */
function withUploads(
  verb: string,
  schema: Record<string, unknown>
): Record<string, unknown> {
  if (!Object.hasOwn(UPLOADS, verb)) return schema
  const u = UPLOADS[verb]!
  const properties = (schema.properties ?? {}) as Record<string, unknown>
  return {
    ...schema,
    properties: {
      ...properties,
      [u.content]: { type: 'string', description: u.what },
      ...(u.name !== 'name'
        ? {
            [u.name]: {
              type: 'string',
              description: 'its file name (the type follows from it)',
            },
          }
        : {}),
      [u.type]: {
        type: 'string',
        description:
          'its content type, when the name does not say (image/png, text/markdown, …)',
      },
    },
  }
}

/** The largest file `attachments` puts in a reply (board #3560). */
const INLINE_MAX_BYTES = 1024 * 1024

/** A type whose bytes read as text: text/*, SVG, JSON. */
const isTextType = (type: string) =>
  type.startsWith('text/') ||
  type === 'image/svg+xml' ||
  type === 'application/json'

/** attachments' schema with this server's `content` switch (board #3560). */
function withDownloads(
  verb: string,
  schema: Record<string, unknown>
): Record<string, unknown> {
  if (verb !== 'attachments') return schema
  return {
    ...schema,
    properties: {
      ...((schema.properties ?? {}) as Record<string, unknown>),
      content: {
        type: 'boolean',
        description: `true: each stored file's contents too, checked against its sha256 — text (text/*, SVG, JSON) as text, anything else as base64; files over ${INLINE_MAX_BYTES} bytes are named, not inlined`,
      },
    },
  }
}

/** A write tool's schema with the required `as` (board #171). */
function withAs(
  schema: Record<string, unknown>,
  machine: string
): Record<string, unknown> {
  const properties = (schema.properties ?? {}) as Record<string, unknown>
  const required = (schema.required ?? []) as string[]
  return {
    ...schema,
    properties: {
      ...properties,
      as: {
        type: 'string',
        description: `Who you are acting as: "${machine} × <repo>", the repo you are working for (e.g. "${machine} × tosijs-3d"). The host verifies the machine; the repo is your word, on every write.`,
      },
    },
    required: [...required, 'as'],
  }
}

function toolError(text: string) {
  return { content: [{ type: 'text', text }], isError: true }
}

function isRequest(m: unknown): m is JsonRpcRequest {
  return (
    !!m &&
    typeof m === 'object' &&
    (m as { jsonrpc?: unknown }).jsonrpc === '2.0' &&
    typeof (m as { method?: unknown }).method === 'string'
  )
}
